import React, { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { createPaymentIntent } from "../services/payments.ts";

// Stripe publishable key is now loaded from environment variable
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "pk_test_51TVC8EJBJ2MQBI9YYQKoFii1BVDQNvJX2ZUbXwRg3q6Gn4qdVkFhWMXc2Ve1EXabCzqoqkeQ9J9p7oOGtnSvVpB000MZ9VvebG"
);

interface StripeCheckoutProps {
  amount: number;
  currency?: string;
  country: string;
  email: string;
  description?: string;
  onSuccess?: (paymentIntentId: string) => void;
  onFailure?: (errorMsg: string) => void;
  // Called when the charge's outcome could not be confirmed client-side (e.g. the network
  // dropped after Stripe processed the request but before the response reached the browser).
  // This is NOT a failure signal - the payment may have succeeded. The caller should
  // reconcile against the backend rather than tell the user the payment failed.
  onUncertain?: (msg: string) => void;
}

const CheckoutForm: React.FC<StripeCheckoutProps> = ({ amount, currency = "usd", country, email, description, onSuccess, onFailure, onUncertain }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    let clientSecret: string;
    try {
      const { data } = await createPaymentIntent(email, country);
      clientSecret = data.clientSecret;
    } catch (err: any) {
      // Failed before any charge was attempted - a genuine failure, safe to report as such.
      const msg = err.response?.data?.detail || err.message || "Could not start payment";
      setError(msg);
      if (onFailure) onFailure(msg);
      setLoading(false);
      return;
    }

    if (!stripe || !elements) {
      setLoading(false);
      return;
    }

    try {
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement)!,
        },
      });
      if (result.error) {
        // Stripe itself returned a decline/error - a trustworthy failure signal.
        const msg = result.error.message || "Payment failed";
        setError(msg);
        if (onFailure) onFailure(msg);
      } else if (result.paymentIntent?.status === "succeeded") {
        setSuccess(true);
        if (onSuccess) onSuccess(result.paymentIntent.id);
      } else {
        const msg = "Payment status unclear, checking...";
        setError(msg);
        if (onUncertain) onUncertain(msg);
      }
    } catch (err: any) {
      // Thrown here (network drop, timeout) does not mean the charge failed - Stripe may have
      // already processed it before the response reached us. Don't declare failure.
      const msg = err.message || "Could not confirm payment status";
      setError(msg);
      if (onUncertain) onUncertain(msg);
      else if (onFailure) onFailure(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-md mx-auto p-4 border rounded shadow">
      {description && <div className="mb-2 text-center font-semibold">{description}</div>}
      <CardElement className="mb-4 p-2 border rounded" />
      <button
        type="submit"
        disabled={!stripe || loading}
        className="bg-pink-500 text-white px-4 py-2 rounded hover:bg-pink-600 disabled:opacity-50"
      >
        {loading
          ? "Processing..."
          : currency === "inr"
            ? `Pay ₹${(amount / 100).toFixed(2)}`
            : currency === "usd"
              ? `Pay $${(amount / 100).toFixed(2)}`
              : `Pay £${(amount / 100).toFixed(2)}`}
      </button>
      {error && <div className="text-red-500 mt-2">{error}</div>}
      {success && <div className="text-green-500 mt-2">Payment successful!</div>}
    </form>
  );
};

const StripeCheckout: React.FC<StripeCheckoutProps> = (props) => (
  <Elements stripe={stripePromise}>
    <CheckoutForm {...props} />
  </Elements>
);

export default StripeCheckout;
